import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../shared/button/Button";
import UserImage from "../shared/userImg/UserImage";
import { IToastContext, ToastContext } from "../../providers/Toast";
import { useDispatch, useSelector } from "react-redux";
import { AuthState } from "../../context/auth/auth/reducer";
import { addUserToContacts, removeUserFromContacts } from "../../context/users/action";
import { UserSnippet } from "../../data/interfaces";
import _environment from "../../_environment/_environment";
import { USER_ROLES } from "../../data/enums";
import Tooltip from "../shared/tooltip/Tooltip";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../utils/analytics";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------

interface Props {
  user: UserSnippet;
  showEmail?: boolean;
  showCompany?: boolean;
  showRole?: boolean;
  showChat?: boolean;
  showAddToContacts?: boolean;
  showPhone?: boolean;
  showDelete?: boolean;
  onDelete?: any;
  showGetAppointment?: boolean;
  onAppointment?: any;
}

const UserBox = ({
                   user,
                   showEmail,
                   showCompany,
                   showRole,
                   showChat,
                   showAddToContacts,
                   showPhone,
                   showDelete,
                   onDelete,
                   showGetAppointment,
                   onAppointment,
                   ...restProps
                 }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Reference to Redux store dispatch function: use it to dispatch actions.
  const dispatch = useDispatch();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alreadyAdded, setAlreadyAdded] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------
  const { user: authUser } = useSelector(({ authReducer }: { authReducer: AuthState }) => {
    return authReducer;
  });

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Dispatch event with updated array of contacts
   * @function
   *
   * @param contact
   * @param remove
   */
  const updateUserContactsInStore = (contact?: string, remove: boolean = false) => {
    if (!authUser || !contact) {
      return;
    }

    let contacts: string[] = authUser.contacts || [];

    if (remove) {
      contacts = contacts.filter((c) => c !== contact);
    } else {
      contacts = [...contacts, contact];
    }

    dispatch({
      type: "USER_UPDATE",
      payload: {
        ...authUser,
        ...{
          contacts: contacts,
        },
      },
    });
  };

  const checkAlreadyInContacts = (): void => {
    try {
      if (!!user.uid) {
        if (authUser?.contacts?.includes(user.uid)) {
          setAlreadyAdded(true);
        }
      } else {
        setAlreadyAdded(false);
      }
    } catch (err) {
      setAlreadyAdded(false);
    }
  };

  /**
   * Add user to contacts
   * @function
   * @async
   */
  const addToContacts = async () => {
    if (alreadyAdded || loading || !authUser) {
      return;
    }

    setLoading(true);

    try {
      await addUserToContacts(user, authUser);
      setAlreadyAdded(true);
      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.contact.added",
        callback: () => {
        },
      });

      updateUserContactsInStore(user.uid);
    } catch (err) {
      openToast({
        type: "error",
        title: "errors.general.title",
        callback: () => {
        },
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove user to contacts
   * @function
   * @async
   */
  const removeFromContacts = async () => {
    if (!alreadyAdded || loading || !authUser) {
      return;
    }

    setLoading(true);

    try {
      await removeUserFromContacts(user, authUser);
      onDelete(user);

      setAlreadyAdded(false);

      openToast({
        type: "success",
        title: "success.general.title",
        message: "success.contact.removed",
        callback: () => {
        },
      });

      updateUserContactsInStore(user.uid, true);
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        callback: () => {
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const openChatLink = () => {
    if (!user.mattermost?.username) {
      return;
    }

    // Track event
    trackAnalyticsEvent(ANALYTICS_EVENTS.USER_CHAT, { from: authUser?.email, to: user.email });

    let username = user.mattermost?.username;

    if (username.charAt(0) !== "@") {
      username = "@" + username;
    }

    window.open(
      `${_environment.mattermost.URL}/${_environment.mattermost.directMessagesPath}/${username}`,
      "_blank",
    );
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    console.log("[UserBox] init");
    checkAlreadyInContacts();
    setVisible(true);
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    console.log("[UserBox] destroy");
    // destroy all
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="user-box">
      <UserImage user={user}/>
      <div className="info">
        <header>
          <h5>
            {user.firstname} {user.lastname}
          </h5>
          <div className="actions">
            {!!showChat && (
              <>
                {!!user.mattermost?.username && (
                  <>
                    <a
                      className="btn btn-link"
                      title={t("pages.chat.title")}
                      onClick={openChatLink}
                      rel="noopener noreferrer"
                      data-tip={t("tooltips.chat.startChat")}
                    >
                      <i className="fas fa-comments"/>
                    </a>
                    <Tooltip/>
                  </>
                )}
              </>
            )}

            {/* Only visitor actions */}
            {authUser?.role === USER_ROLES.VISITOR && (
              <>
                {!!showGetAppointment && !!onAppointment && (
                  <Button
                    tooltip={t("tooltips.appointments.request")}
                    title={t("pages.standDetail.tabs.appointments.form.submit")}
                    className="btn btn-link"
                    onClick={() => {
                      onAppointment(user);
                    }}
                  >
                    <i className="far fa-calendar-plus"/>
                  </Button>
                )}

                {!!showAddToContacts && !alreadyAdded && (
                  <Button
                    tooltip={t("tooltips.contacts.add")}
                    className="btn btn-link"
                    onClick={() => {
                      addToContacts();
                    }}
                  >
                    <i className="fas fa-user-plus"/>
                  </Button>
                )}
                {!!showAddToContacts && !!alreadyAdded && (
                  <Button className="btn btn-link" tooltip={t("tooltips.contacts.alreadyAdded")}>
                    <i className="fas fa-user-check"/>
                  </Button>
                )}

                {!!showDelete && (
                  <Button
                    className="btn btn-link btn-circle"
                    tooltip={t("tooltips.contacts.remove")}
                    onClick={() => {
                      removeFromContacts();
                    }}
                  >
                    <i className="fas fa-user-minus"/>
                  </Button>
                )}
              </>
            )}
          </div>
        </header>
        <div className="business">
          {!!showRole && (
            <div className="value">
              <i className="fas fa-briefcase"/>
              <span>{user.company_role}</span>
            </div>
          )}

          {!!showCompany && (
            <div className="value">
              <i className="fas fa-thumbtack"/>
              <span>{user.company}</span>
            </div>
          )}

          {!!showEmail && (
            <div className="value">
              <i className="far fa-envelope"/>
              <span>{user.email}</span>
            </div>
          )}

          {!!showPhone && !!user.phone_number && (
            <div className="value">
              <i className="fas fa-mobile-alt"/>
              <span>{user.phone_number}</span>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};
export default UserBox;
